<script setup lang="ts">
import { ref, computed, watch } from "vue";
import SidebarNavigation from "../../sidebar/sidebarNavigation.vue";

const props = withDefaults(
  defineProps<{
    filter: any;
    activeValue: any;
  }>(),
  {
    filter: () => ({}),
    activeValue: () => [],
  }
);

const emit = defineEmits<{
  (e: "update", value: any): void;
}>();

const toggleValue = (val: string) => {

  let newValue = Array.isArray(props.activeValue) ? [...props.activeValue] : [];

  if (typeof props.filter.fancy_colors != "undefined" && Object.keys(props.filter.fancy_colors).includes(val)) {
    newValue = [val]
  } else {
    const index = newValue.indexOf(val);
    if (index > -1) {
      newValue.splice(index, 1);
    } else {
      newValue.push(val);
    }

  }
  emit("update", newValue);
};

const getImageUrl = (val: string) => {
  if (!props.filter.image) return null;
  const processedVal = props.filter.lowercaseImage !== false ? val.toLowerCase() : val;
  return props.filter.image.replace("{value}", processedVal);
};

const isActive = (val: string) => {
  return Array.isArray(props.activeValue) && props.activeValue.includes(val);
};

const itemKeys = computed(() => Object.keys(props.filter.values || {}));
const itemRefs = ref<HTMLElement[]>([]);
const scrollContainer = ref<HTMLElement | null>(null);
const manualIndex = ref(0);

const currentIndex = computed({
  get: () => manualIndex.value,
  set: (val) => {
    manualIndex.value = val;
    scrollToIndex(val);
  }
});

const scrollToIndex = (index: number) => {
  const el = itemRefs.value[index];
  if (el && scrollContainer.value) {
    const container = scrollContainer.value;
    const elLeft = el.offsetLeft;
    const elWidth = el.clientWidth;
    const containerWidth = container.clientWidth;
    const targetScrollLeft = elLeft - (containerWidth / 2) + (elWidth / 2);
    container.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
  }
};

watch(() => props.activeValue, () => {
  const keys = itemKeys.value;
  const active = Array.isArray(props.activeValue) ? props.activeValue[0] : props.activeValue;
  const idx = keys.indexOf(active?.toString() || '');
  if (idx >= 0) manualIndex.value = idx;
}, { immediate: true });

const onScroll = () => {
  if (!scrollContainer.value) return;
  const container = scrollContainer.value;
  const containerCenter = container.scrollLeft + container.clientWidth / 2;

  let closestIndex = 0;
  let minDistance = Infinity;

  itemRefs.value.forEach((el, index) => {
    if (!el) return;
    const elCenter = el.offsetLeft + el.clientWidth / 2;
    const distance = Math.abs(elCenter - containerCenter);
    if (distance < minDistance) {
      minDistance = distance;
      closestIndex = index;
    }
  });

  manualIndex.value = closestIndex;
};
</script>

<template>
  <div class="flex flex-col">
    <div
      ref="scrollContainer"
      class="overflow-x-auto no-scrollbar scroll-smooth"
      @scroll="onScroll"
    >
      <div class="flex gap-8 pb-4 px-2 items-center">
        <div
          v-for="(label, val, index) in filter.values"
          :key="val"
          :ref="el => { if (el && index !== undefined) itemRefs[index] = el as any }"
          class="flex flex-col items-center cursor-pointer min-w-fit justify-end"
          @click="toggleValue(val.toString())"
        >
          <div
            class="w-16 h-16 rounded-full flex items-center justify-center mb-2 transition-all duration-300 overflow-hidden"
            :class="isActive(val.toString()) ? 'shadow-md w-20 h-20' : ''"
          >
            <template v-if="filter.image">
              <img
                :src="getImageUrl(val.toString())"
                :alt="label"
                class="w-16 h-16 object-contain"
              />
            </template>
            <div v-else class="text-xs font-medium text-gray-700">
              {{ val }}
            </div>
          </div>
          <span class="text-[11px] text-gray-500 tracking-wide whitespace-nowrap"
                :class="isActive(val.toString()) ? 'font-bold' : ''"
          >{{ label }}</span>
        </div>
      </div>
    </div>
    <sidebar-navigation
      :total="itemKeys.length"
      v-model="currentIndex"
      :container="scrollContainer"
    />
  </div>
</template>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>